import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useI18n } from "../../i18n/I18nContext";

const API_BASE_URL = process.env.REACT_APP_API_URL;

function slugify(text) {
  return text.toString().toLowerCase().trim()
    .replace(/\s+/g, "-").replace(/[^\w-]+/g, "").replace(/--+/g, "-");
}

function buildTreeList(items) {
  const parents = items
    .filter((c) => !c.parent_id)
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

  const result = [];
  for (const parent of parents) {
    result.push(parent);
    const children = items
      .filter((c) => c.parent_id === parent.id)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    result.push(...children);
  }
  return result;
}

function SortableRow({ cat, allCategories, onEdit, onDelete }) {
  const { t } = useI18n();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: cat.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    background: isDragging ? "#f0f9ff" : undefined,
  };

  const isChild = !!cat.parent_id;

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={isChild ? "bg-gray-50/80 hover:bg-gray-100/60" : "bg-white hover:bg-gray-50"}
    >
      <td
        className="px-3 py-3 text-gray-300 cursor-grab hover:text-gray-500 active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="5" cy="4" r="1.2" /><circle cx="11" cy="4" r="1.2" />
          <circle cx="5" cy="8" r="1.2" /><circle cx="11" cy="8" r="1.2" />
          <circle cx="5" cy="12" r="1.2" /><circle cx="11" cy="12" r="1.2" />
        </svg>
      </td>

      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {isChild ? (
            <div className="shrink-0" style={{ width: 20 }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M4 0 L4 12 L18 12" stroke="#d1d5db" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
            </div>
          ) : (
            <div style={{ width: 20 }} className="shrink-0" />
          )}
          <span className={`font-medium ${isChild ? "text-gray-500 text-sm" : "text-gray-900"}`}>
            {cat.name}
          </span>
          {!isChild && (
            <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-500">
              {t("pai")}
            </span>
          )}
        </div>
      </td>

      <td className="px-4 py-3 text-gray-400 text-xs">{cat.slug}</td>

      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-2">
          <button
            onClick={() => onEdit(cat)}
            className="rounded-md border px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-100"
          >
            {t("Editar")}
          </button>
          <button
            onClick={() => onDelete(cat)}
            className="rounded-md border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
          >
            {t("Apagar")}
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function AdminCategories() {
  const { t } = useI18n();
  const token = localStorage.getItem("token");

  const api = useMemo(() => axios.create({
    baseURL: API_BASE_URL,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  }), [token]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [q, setQ] = useState("");

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [parentId, setParentId] = useState("");

  const [editing, setEditing] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const fetchCategories = async () => {
    setErrorMsg("");
    setLoading(true);
    try {
      const res = await api.get(`/api/v1/categories`, { params: q ? { q } : {} });
      const data = (res.data?.data || []).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      setItems(data);
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro ao carregar categorias."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCategories(); }, []); // eslint-disable-line

  useEffect(() => {
    setSlug((prev) => {
      if (prev && prev !== slugify(name)) return prev;
      return slugify(name);
    });
  }, [name]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // opera sobre a lista em árvore para manter a ordem visual
    const treeList = buildTreeList(items);
    const oldIndex = treeList.findIndex((i) => i.id === active.id);
    const newIndex = treeList.findIndex((i) => i.id === over.id);
    const reordered = arrayMove(treeList, oldIndex, newIndex).map((item, idx) => ({
      ...item,
      position: idx,
    }));

    setItems(reordered);

    try {
      await api.post(`/api/admin/categories/reorder`, {
        categories: reordered.map((i) => ({ id: i.id, position: i.position })),
      });
    } catch {
      setErrorMsg(t("Erro ao guardar ordem. Tenta novamente."));
      await fetchCategories();
    }
  };

  const onCreate = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSaving(true);
    try {
      const res = await api.post(`/api/admin/categories`, {
        name,
        slug: slug || undefined,
        parent_id: parentId || null,
      });
      const newCategory = res.data?.data;
      setItems((prev) => [...prev, { ...newCategory, position: newCategory.position ?? prev.length }]);
      setName("");
      setSlug("");
      setParentId("");
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro ao criar categoria."));
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setErrorMsg("");
    setSaving(true);
    try {
      await api.put(`/api/admin/categories/${editing.id}`, {
        name: editing.name,
        slug: editing.slug || undefined,
        parent_id: editing.parent_id || null,
      });
      setEditing(null);
      await fetchCategories();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro ao atualizar categoria."));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (cat) => {
    if (!window.confirm(`${t("Apagar")} "${cat.name}"?`)) return;
    try {
      await api.delete(`/api/admin/categories/${cat.id}`);
      await fetchCategories();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || t("Erro ao apagar categoria."));
    }
  };

  const parentOptions = (excludeId = null) =>
    items.filter((c) => !c.parent_id && c.id !== excludeId);

  const treeItems = buildTreeList(items);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("Categorias")}</h1>
            <p className="text-sm text-gray-600">
              {t("Arrasta para reordenar. Define categoria pai para criar subcategorias.")}
            </p>
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); fetchCategories(); }}
            className="flex gap-2"
          >
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("Pesquisar…")}
              className="w-full sm:w-64 rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
            />
            <button
              type="submit"
              className="rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
            >
              {t("Pesquisar")}
            </button>
          </form>
      </div>

      {errorMsg && (
        <div className="mt-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-700">{errorMsg}</div>
      )}

      {/* Criar */}
      <div className="mt-6 rounded-lg border bg-white p-4 shadow-sm">
        <h2 className="text-base font-semibold text-gray-900">{t("Criar categoria")}</h2>
        <form onSubmit={onCreate} className="mt-3 grid gap-3 sm:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t("Nome")}</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
              placeholder={t("ex: Racks")}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t("Slug")}</label>
            <input
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
              placeholder={t("gerado automaticamente")}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">{t("Categoria pai")}</label>
            <select
              value={parentId}
              onChange={(e) => setParentId(e.target.value)}
              className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
            >
              <option value="">{t("— Nenhuma (categoria raiz) —")}</option>
              {parentOptions().map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={saving}
              className="w-full rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
            >
              {saving ? t("A guardar…") : t("Criar")}
            </button>
          </div>
        </form>
      </div>

      {/* Lista */}
      <div className="mt-6 overflow-hidden rounded-lg border bg-white shadow-sm">
        <div className="border-b px-4 py-3 flex items-center justify-between">
          <h2 className="text-base font-semibold text-gray-900">{t("Lista de categorias")}</h2>
          <span className="text-xs text-gray-400">{t("Arrasta pela coluna ⠿ para reordenar")}</span>
        </div>

        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-500">{t("A carregar…")}</div>
        ) : items.length === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-500">{t("Nenhuma categoria encontrada.")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-3 py-3 w-8"></th>
                  <th className="px-4 py-3">{t("Nome")}</th>
                  <th className="px-4 py-3">{t("Slug")}</th>
                  <th className="px-4 py-3 text-right">{t("Ações")}</th>
                </tr>
              </thead>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={treeItems.map((i) => i.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody className="divide-y divide-gray-100">
                    {treeItems.map((cat) => (
                      <SortableRow
                        key={cat.id}
                        cat={cat}
                        allCategories={items}
                        onEdit={(c) => setEditing({ ...c, parent_id: c.parent_id ?? "" })}
                        onDelete={onDelete}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
          </div>
        )}
      </div>

      {/* Modal editar */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-5 shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{t("Editar categoria")}</h3>
                <p className="text-sm text-gray-500">ID #{editing.id}</p>
              </div>
              <button
                onClick={() => setEditing(null)}
                className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
              >
                ✕
              </button>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t("Nome")}</label>
                <input
                  value={editing.name}
                  onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">{t("Slug")}</label>
                <input
                  value={editing.slug}
                  onChange={(e) => setEditing((s) => ({ ...s, slug: e.target.value }))}
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-gray-700">{t("Categoria pai")}</label>
                <select
                  value={editing.parent_id ?? ""}
                  onChange={(e) => setEditing((s) => ({ ...s, parent_id: e.target.value || null }))}
                  className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring"
                >
                  <option value="">{t("— Nenhuma (categoria raiz) —")}</option>
                  {parentOptions(editing.id).map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                className="rounded-md border px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
              >
                {t("Cancelar")}
              </button>
              <button
                onClick={saveEdit}
                disabled={saving}
                className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
              >
                {saving ? t("A guardar…") : t("Guardar")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}